import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';
import { lazyWithRetry } from './lib/lazyWithRetry';

// Context
import { AuthProvider, useAuth } from './components/context/AuthContext';
import { ThemeProvider } from './components/context/ThemeContext';

// Admin Panel (isolated auth) — these stay eagerly imported because
// they live on a separate URL space (`/admin/*`) and have their own
// auth provider; lazy-loading them gives no real win since the user
// can only reach them via direct URL.
import { AdminAuthProvider } from './admin/AdminAuthContext';
import { AdminLoginPage } from './admin/AdminLoginPage';
import { AdminForgotPasswordPage } from './admin/AdminForgotPasswordPage';
import { AdminDashboardPage } from './admin/AdminDashboardPage';
import { AdminBrandingPage } from './admin/AdminBrandingPage';
import { AdminNotificationsPage } from './admin/AdminNotificationsPage';
import { AdminLicensesPage } from './admin/AdminLicensesPage';
import { AdminPaymentIntegrationsPage } from './admin/AdminPaymentIntegrationsPage';
import { AdminBillingProfilePage } from './admin/AdminBillingProfilePage';
import { AdminInvoicingPage } from './admin/AdminInvoicingPage';
import { AdminInvoiceDetailPage } from './admin/AdminInvoiceDetailPage';
import { AdminWalletsPage } from './admin/AdminWalletsPage';
import { AdminGstinProvidersPage } from './admin/AdminGstinProvidersPage';
import { AdminWalletDetailPage } from './admin/AdminWalletDetailPage';
import { AdminLicensePricingPage } from './admin/AdminLicensePricingPage';
import { AdminEmailIntegrationsPage } from './admin/AdminEmailIntegrationsPage';
import { AdminWhatsappIntegrationsPage } from './admin/AdminWhatsappIntegrationsPage';
import { AdminEInvoiceGSPPage } from './admin/AdminEInvoiceGSPPage';
import { AdminDiagnosticsPage } from './admin/AdminDiagnosticsPage';
import { AdminLicenseLogsPage } from './admin/AdminLicenseLogsPage';
import { AdminEmailLogsPage } from './admin/AdminEmailLogsPage';
import { AdminEmailTemplatesPage } from './admin/AdminEmailTemplatesPage';
import { AdminWebhooksPage } from './admin/AdminWebhooksPage';
import { AdminProfilePage } from './admin/AdminProfilePage';
import { AdminTeamPage } from './admin/AdminTeamPage';
import { AdminCentralLogsPage } from './admin/AdminCentralLogsPage';
import { AdminHelpCenterPage } from './admin/AdminHelpCenterPage';
import { AdminUserConsolePage } from './admin/AdminUserConsolePage';
import { AdminUserAuditLogsPage } from './admin/AdminUserAuditLogsPage';
import { AdminDemoLeadsPage } from './admin/AdminDemoLeadsPage';
import { AdminKBEditorPage } from './admin/AdminKBEditorPage';
import ImpersonateLandingPage from './pages/ImpersonateLandingPage';
import { InviteAcceptPage } from './components/pages/InviteAcceptPage';

// Public auth flow
import { AuthPage } from './components/auth/AuthPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import { Navigation } from './components/Navigation';
import { TopBar } from './components/TopBar';
import { ProRoute } from './components/ProRoute';
import WalletPage from './components/pages/WalletPage';
import { Breadcrumbs } from './components/Breadcrumbs';
import { BreadcrumbProvider } from './components/context/BreadcrumbContext';
import { MobileNavProvider } from './components/context/MobileNavContext';
import { SandboxExpiryBanner } from './components/SandboxExpiryBanner';
import { useBrowserMetadata } from './hooks/useBrowserMetadata';

// =============================================================================
// Lazy-loaded tenant pages — each one becomes its own webpack chunk so the
// initial bundle only ships the Dashboard skeleton + Navigation. Switching
// to a different route fetches a small JS chunk on demand. Big wins for
// users who spend most of their time on a single section (e.g. Invoices)
// because they never download the Inventory / Reports / Admin code.
// =============================================================================
const Dashboard = lazy(lazyWithRetry(() => import('./components/pages/Dashboard').then(m => ({ default: m.Dashboard }))));
const CustomerManagement = lazy(lazyWithRetry(() => import('./components/pages/CustomerManagement').then(m => ({ default: m.CustomerManagement }))));
const ProductManagement = lazy(lazyWithRetry(() => import('./components/pages/ProductManagement').then(m => ({ default: m.ProductManagement }))));
const InvoiceManagement = lazy(lazyWithRetry(() => import('./components/pages/InvoiceManagement').then(m => ({ default: m.InvoiceManagement }))));
const CreditNoteManagement = lazy(lazyWithRetry(() => import('./components/pages/CreditNoteManagement').then(m => ({ default: m.CreditNoteManagement }))));
const DebitNoteManagement = lazy(lazyWithRetry(() => import('./components/pages/DebitNoteManagement').then(m => ({ default: m.DebitNoteManagement }))));
const QuotationManagement = lazy(lazyWithRetry(() => import('./components/pages/QuotationManagement').then(m => ({ default: m.QuotationManagement }))));
const ProformaInvoiceManagement = lazy(lazyWithRetry(() => import('./components/pages/ProformaInvoiceManagement').then(m => ({ default: m.ProformaInvoiceManagement }))));
const VendorManagement = lazy(lazyWithRetry(() => import('./components/pages/VendorManagement').then(m => ({ default: m.VendorManagement }))));
const PurchaseOrderManagement = lazy(lazyWithRetry(() => import('./components/pages/PurchaseOrderManagement').then(m => ({ default: m.PurchaseOrderManagement }))));
const PurchaseInvoiceManagement = lazy(lazyWithRetry(() => import('./components/pages/PurchaseInvoiceManagement').then(m => ({ default: m.PurchaseInvoiceManagement }))));
const ReportsManagement = lazy(lazyWithRetry(() => import('./components/pages/ReportsManagement').then(m => ({ default: m.ReportsManagement }))));
const SettingsManagement = lazy(lazyWithRetry(() => import('./components/pages/SettingsManagement').then(m => ({ default: m.SettingsManagement }))));
const PlanOverviewPage = lazy(lazyWithRetry(() => import('./components/pages/PlanOverviewPage').then(m => ({ default: m.PlanOverviewPage }))));
const InventoryManagement = lazy(lazyWithRetry(() => import('./components/pages/InventoryManagement').then(m => ({ default: m.InventoryManagement }))));
const CustomerSupportPage = lazy(lazyWithRetry(() => import('./components/pages/CustomerSupportPage').then(m => ({ default: m.CustomerSupportPage }))));
const EInvoicePage = lazy(lazyWithRetry(() => import('./components/pages/EInvoicePage').then(m => ({ default: m.EInvoicePage }))));
const DeliveryChallansListPage = lazy(lazyWithRetry(() => import('./components/pages/DeliveryChallansListPage').then(m => ({ default: m.DeliveryChallansListPage }))));
const DeliveryChallanCreatePage = lazy(lazyWithRetry(() => import('./components/pages/DeliveryChallanCreatePage').then(m => ({ default: m.DeliveryChallanCreatePage }))));
const EwayBillsListPage = lazy(lazyWithRetry(() => import('./components/pages/EwayBillsListPage').then(m => ({ default: m.EwayBillsListPage }))));
const EwayBillCreatePage = lazy(lazyWithRetry(() => import('./components/pages/EwayBillCreatePage').then(m => ({ default: m.EwayBillCreatePage }))));

// Toast notifications
import { Toaster } from 'sonner';
import ToastCloseEnhancer from './components/ToastCloseEnhancer';

// Shared spinner used while a lazy chunk is being downloaded for the
// first time. Subsequent route visits are instant because the chunk
// is cached by the browser.
function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading-fallback">
      <div
        className="animate-spin rounded-full h-12 w-12 border-b-2"
        style={{ borderColor: 'var(--theme-primary, #1a1a2e)' }}
      />
    </div>
  );
}

import useTallyShortcuts from './hooks/useTallyShortcuts';

function MainApp() {
  // The whole authenticated app shell — sidebar + suspense-wrapped routes.
  // Navigation no longer needs activeTab props; it reads the URL itself.
  // BreadcrumbProvider lets nested pages push an "extra" crumb (e.g. the
  // document number while editing) without prop-drilling.
  useTallyShortcuts();
  return (
    <MobileNavProvider>
      <BreadcrumbProvider>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
          <TopBar />
          <Navigation />
          <SandboxExpiryBanner />
          <main className="md:ml-64 pt-20 pb-4 md:pb-6 px-3 sm:px-6 lg:px-8 text-slate-700 dark:text-slate-200">
            <div className="max-w-7xl mx-auto pt-2">
            <Breadcrumbs />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="customers" element={<CustomerManagement />} />
                <Route path="vendors" element={<VendorManagement />} />
                <Route path="products" element={<ProductManagement />} />
                <Route path="invoices" element={<InvoiceManagement />} />
                {/* AI Omnibar redirects here after a successful invoice
                    create. InvoiceManagement reads the :focusId param and
                    auto-opens the preview dialog. */}
                <Route path="sales/invoices/:focusId" element={<InvoiceManagement />} />
                <Route path="quotations" element={<QuotationManagement />} />
                <Route path="proforma" element={<ProformaInvoiceManagement />} />
                <Route path="credit-notes" element={<CreditNoteManagement />} />
                <Route path="einvoice" element={<ProRoute><EInvoicePage /></ProRoute>} />
                <Route path="wallet" element={<WalletPage />} />
                {/* Logistics tracking — Delivery Challans visible to all,
                    E-Way Bills Pro-gated per product spec. */}
                <Route path="sales/delivery-challans" element={<DeliveryChallansListPage />} />
                <Route path="sales/delivery-challans/create" element={<DeliveryChallanCreatePage />} />
                <Route path="sales/delivery-challans/:id/edit" element={<DeliveryChallanCreatePage />} />
                <Route
                  path="sales/eway-bills"
                  element={
                    <ProRoute message="This feature is exclusive to Pro License users.">
                      <EwayBillsListPage />
                    </ProRoute>
                  }
                />
                <Route
                  path="sales/eway-bills/create"
                  element={
                    <ProRoute message="This feature is exclusive to Pro License users.">
                      <EwayBillCreatePage />
                    </ProRoute>
                  }
                />
                {/* `/purchases` is a friendlier landing alias for the purchases section. */}
                <Route path="purchases" element={<Navigate to="/purchase-orders" replace />} />
                <Route path="purchase-orders" element={<PurchaseOrderManagement />} />
                <Route path="purchase-invoices" element={<PurchaseInvoiceManagement />} />
                <Route path="debit-notes" element={<DebitNoteManagement />} />
                <Route path="inventory" element={<InventoryManagement />} />
                {/* Reports is now sidebar-driven; the bare /reports URL
                    lands on Basic Reports by default. */}
                <Route path="reports" element={<Navigate to="/reports/basic" replace />} />
                <Route path="reports/basic" element={<ReportsManagement view="basic" />} />
                <Route path="reports/gst-compliance" element={<ReportsManagement view="gst-compliance" />} />
                <Route path="settings" element={<SettingsManagement />} />
                <Route path="settings/plan-overview" element={<PlanOverviewPage />} />
                <Route path="plan-overview" element={<Navigate to="/settings/plan-overview" replace />} />
                <Route path="dashboard/support" element={<CustomerSupportPage />} />
                <Route path="support" element={<Navigate to="/dashboard/support" replace />} />
                {/* Unknown path inside the authenticated shell → dashboard. */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </div>
        </main>
      </div>
    </BreadcrumbProvider>
   </MobileNavProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <AdminAuthProvider>
            <BrowserRouter>
              <div className="App">
                <AppContent />
                <Toaster
                  position="top-right"
                  offset={72}
                  closeButton
                  toastOptions={{
                    // Reserve right padding so the close X never overlaps
                    // the toast title / action button. The close X itself
                    // is repositioned to the top-right and made fully
                    // opaque (sonner's default fades it in on hover only).
                    classNames: {
                      toast:
                        'group toast group-[.toaster]:pr-10 group-[.toaster]:bg-white group-[.toaster]:text-slate-900 group-[.toaster]:border group-[.toaster]:border-slate-200 group-[.toaster]:shadow-lg',
                      closeButton:
                        '!left-auto !right-2 !top-2 !translate-x-0 !opacity-100 !bg-white !border-slate-200 !text-slate-500 hover:!text-slate-900 hover:!bg-slate-100 !w-[22px] !h-[22px] !rounded-full !transition-colors',
                    },
                  }}
                />
                {/* Guarantees a close X on EVERY toast — including the
                    types Sonner intentionally excludes (loading,
                    promise-while-pending, and custom JSX toasts). */}
                <ToastCloseEnhancer />
              </div>
            </BrowserRouter>
          </AdminAuthProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Single QueryClient instance for the app. Defaults tuned for this
// mostly-CRUD billing app: 2-min stale window is enough to dedupe route
// switches, mutations explicitly invalidate their keys.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function AppContent() {
  const { loading, user } = useAuth();
  const hasToken = !!localStorage.getItem('token');
  // Pull the SuperAdmin-controlled site_title_string + favicon_image_url
  // and patch document.title + <link rel="icon"> on every mount. Re-runs
  // when the admin saves new branding (event-bus on `site-config-updated`).
  useBrowserMetadata();

  // PERFORMANCE: don't block the entire UI on /api/auth/me. If the user has
  // a token in localStorage we render the authenticated app shell IMMEDIATELY
  // — sidebar, breadcrumb and Suspense fallback take over. The auth call
  // resolves in parallel and `user` populates with no flicker. If the call
  // 401s, AuthContext clears the token and a subsequent render redirects to
  // /auth. We ONLY show the fullscreen spinner when we have no token AND
  // auth is still loading (cold-load with no prior session).
  const isAuthenticated = user || hasToken;

  if (loading && !hasToken) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-32 w-32 border-b-2"
          style={{ borderColor: 'var(--theme-primary, #1a1a2e)' }}
        />
      </div>
    );
  }

  return (
    <Routes>
      {/* Admin panel (isolated auth space) */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/branding" element={<AdminBrandingPage />} />
      <Route path="/admin/notifications" element={<AdminNotificationsPage />} />
      <Route path="/admin/licenses" element={<AdminLicensesPage />} />
      <Route path="/admin/payments" element={<AdminPaymentIntegrationsPage />} />
      <Route path="/admin/billing-profile" element={<AdminBillingProfilePage />} />
      <Route path="/admin/invoicing" element={<AdminInvoicingPage />} />
      <Route path="/admin/invoicing/invoices/:id" element={<AdminInvoiceDetailPage />} />
      <Route path="/admin/wallets" element={<AdminWalletsPage />} />
      <Route path="/admin/gstin-providers" element={<AdminGstinProvidersPage />} />
      <Route path="/admin/wallets/:orgId" element={<AdminWalletDetailPage />} />
      <Route path="/admin/license-pricing" element={<AdminLicensePricingPage />} />
      <Route path="/admin/email-integrations" element={<AdminEmailIntegrationsPage />} />
      <Route path="/admin/whatsapp-integrations" element={<AdminWhatsappIntegrationsPage />} />
      <Route path="/admin/einvoice-gsp" element={<AdminEInvoiceGSPPage />} />
      <Route path="/admin/diagnostics" element={<AdminDiagnosticsPage />} />
      <Route path="/admin/license-logs" element={<AdminLicenseLogsPage />} />
      <Route path="/admin/email-logs" element={<AdminEmailLogsPage />} />
      <Route path="/admin/email-templates" element={<AdminEmailTemplatesPage />} />
      <Route path="/admin/webhooks" element={<AdminWebhooksPage />} />
      <Route path="/admin/profile" element={<AdminProfilePage />} />
      <Route path="/admin/team" element={<AdminTeamPage />} />
      <Route path="/admin/central-logs" element={<AdminCentralLogsPage />} />
      <Route path="/admin/help-center" element={<AdminHelpCenterPage />} />
      <Route path="/admin/user-console" element={<AdminUserConsolePage />} />
      <Route path="/admin/user-audit-logs" element={<AdminUserAuditLogsPage />} />
      <Route path="/admin/demo-leads" element={<AdminDemoLeadsPage />} />
      <Route path="/admin/kb-editor" element={<AdminKBEditorPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />

      {/* Impersonation handshake — opened in a new tab by the User
          Troubleshooting Console. Stashes the short-lived JWT into
          localStorage then redirects to /dashboard. */}
      <Route path="/auth/impersonate" element={<ImpersonateLandingPage />} />

      {/* Public flow */}
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Authenticated tenant app — all sub-routes mount inside MainApp.
          The catch-all "/*" makes deep-linking work: refreshing
          /invoices (or any other path) lands inside MainApp which then
          mounts the matching route, instead of bouncing back to /. */}
      <Route
        path="/*"
        element={isAuthenticated ? <MainApp /> : <Navigate to="/auth" replace />}
      />
    </Routes>
  );
}

export default App;
